import { ChangeDetectorRef, Component, DOCUMENT, ElementRef, HostListener, Inject, OnDestroy, ViewChild } from '@angular/core';
import { RoutesPath } from '../../../../shared/constants/routes-path';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { ValidationMessageComponent } from '../../../../shared/components/validation-message/validation-message.component';
import { MembershipActivitiesComponent } from './membership-activities/membership-activities.component';
import { MembershipOverviewComponent } from './membership-overview/membership-overview.component';
import { MembershipOwnersComponent } from './membership-owners/membership-owners.component';
import { MembershipUsersComponent } from './membership-users/membership-users.component';
import { MembershipYouComponent } from './membership-you/membership-you.component';
import { ApiEndPoints, ApiRoot } from '../../../../shared/constants/api-endpoints';
import { ApiService } from '../../../../core/services/api.service';
import { CSNMemberResponse } from '../../../../shared/models/member-response.model';
import { SharedService } from '../../../../shared/services/shared.service';
import { SkeletonLoaderComponent } from "../../../../shared/components/skeleton-loader/skeleton-loader.component";
import { Utils } from '../../../../Utils/utils';
import { FormsModule } from "@angular/forms";
import { DocumentServiceType, FieldValue, mapServicesFromFlags, ServiceName } from '../../../../shared/constants/services.enum';
import { SrStatus } from '../../../../shared/constants/status.enum';
import { UpsertRequestModel } from '../../services/add-business/models/link-member-query-request.model';
import { environment } from '../../../../../environments/environment';
import { AccountUser } from '../../../../shared/models/account-user.model';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { AccountManagementSharedService } from '../services/account-management-shared.service';
import { DcMethodValues } from '../../../../shared/constants/dc-method.constants';
import { NgClass } from '@angular/common';
import { finalize } from 'rxjs';
import { ScreenLoaderComponent } from "../../../../shared/components/screen-loader/screen-loader.component";
import { InlineCalendarComponent } from "../../../../shared/components/inline-calendar/inline-calendar.component";
import { PLATINUM_POWER_BI, buildPlatinumReportEmbedUrl } from '../../../../shared/constants/platinum-report.constants';

@Component({
  selector: 'app-membership-detail',
  imports: [TranslateModule, ButtonComponent, ValidationMessageComponent, MembershipOverviewComponent, 
    MembershipYouComponent, MembershipUsersComponent, MembershipActivitiesComponent, 
    MembershipOwnersComponent, SkeletonLoaderComponent, FormsModule, ConfirmationModalComponent,
  NgClass, ScreenLoaderComponent, InlineCalendarComponent],
  templateUrl: './membership-detail.component.html',
  styleUrl: './membership-detail.component.scss'
})
export class MembershipDetailComponent implements OnDestroy {
  @ViewChild('powerBiContainer') powerBiContainer?: ElementRef<HTMLDivElement>;
  tab: string = 'overview';
  tabs: string[] = ['overview', 'owners', 'activities', 'users', 'you']
  membership: CSNMemberResponse | null = null;
  showAddBusinessMessage = false;
  memberNumber: string = "";
  requestedTab: string | null = null;
  loader: boolean = false;
  reportDownloadLoader: boolean = false;
  viewReportLoader: boolean = false;
  showReportFilterModal: boolean = false;
  lang: string = 'en';
  loginUser: any = "";
  today: Date = new Date();
  showDateFromCalendar: boolean = false;
  showDateToCalendar: boolean = false;
  selectedDateFrom: Date | null = null;
  selectedDateTo: Date | null = null;
  reportDateRangeError: boolean = false;
  reportDateRequiredError: boolean = false;
  showPowerBiReport: boolean = false;
  powerBiError: boolean = false;
  isPowerBiFullscreen: boolean = false;
  private embeddedPowerBiReport: any = null;
  flags = {
    accountManagementEnabled:
      environment.mVP1Settings?.accountManagementEnabled ?? true,
  };
  isPrimaryUser: boolean = false;
  get isPlatinumMember(): boolean {
    return this.isPlatinumFlag((this.membership as any)?.['DC Platinum Member Flag']);
  }

  // This method is used to initialize the class instance using router, route, api, and shared service
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private api: ApiService,
    private sharedService: SharedService,
    private accountManagementSharedService: AccountManagementSharedService,
    private cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.lang = this.sharedService.getCurrentLang();
    this.loginUser = this.sharedService.getUserProfile();
    this.requestedTab = (this.route.snapshot.queryParamMap.get('tab') || '')?.toLowerCase() || null;
    this.route.params.subscribe(params => {
      if (params['csn']) {
        this.memberNumber = params['csn'] || "";
        this.loader = true;
        const userDetails: any = this.sharedService.getUserProfile();
        let accounts = Utils.checkIsArray(userDetails?.Account) ?? [];
        let findAcc = accounts.find((acc) => acc.CSN == this.memberNumber);
        if (findAcc) {
          this.getMemberDetail();
          this.getLinkedUsers();
        }
      } else {
        this.router.navigate([RoutesPath.DASHBOARD]);
      }
    });

    this.isPrimaryUser = this.sharedService.checkPrimaryUser(this.memberNumber);

    this.getPendingAccessRequest();
  }

  ngOnDestroy(): void {
    this.setPowerBiFullscreen(false);
    this.resetEmbeddedPowerBiReport();
  }

  @HostListener('document:keydown.escape')
  handleEscapeKey(): void {
    if (this.isPowerBiFullscreen) {
      this.setPowerBiFullscreen(false);
    }
  }

  getMemberDisplayStatus(member: any): string {
    return this.sharedService.getMemberDisplayStatus(member);
  }
  ownerList: any[] = [];
  licenseActivities: any[] = [];
  // This method is used to get member detail
  getMemberDetail() {
    this.loader = true;
    let requestPayload = {
      "body": {
        "ProcessName": "DC Get Member Details Web WF",
        "DC Channel": this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
        "DC Method": DcMethodValues.MEMBERSHIP_DETAIL,
        "IOName": "DC Get Acc Details IO",
        "CSN": this.memberNumber
      }
    }
    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.GET_MEMBER_DETAIL_BY_CSN, requestPayload)
      .subscribe({
        // This method is used to handle the next callback using response
        next: (response: any) => {
          this.loader = false;
          if (response["Error Code"] == "" && response["Error Message"] == "") {
            if (response.SiebelMessage?.["DC Account Data Transfer"]) {
              this.membership = response.SiebelMessage?.["DC Account Data Transfer"];
              this.updateTabs();
              this.applyRequestedTab();

              let serviceRequest = response.SiebelMessage?.["DC Account Data Transfer"];
              // --- Owners / Party Relationship ---
              if (serviceRequest["Party Relationship To"]) {
                const relationships = Utils.checkIsArray(serviceRequest["Party Relationship To"]);
                relationships?.map((p: any, index: number) => {
                  const jobTitle = p["DC Job Title"] || "Owner";
                  const isAuthSignatory = p["DC Authorized Signatory"] || "N";
                  const role = this.getRolesFromRelationshipJobTitle(p["Relationship Type"], jobTitle, isAuthSignatory);

                  // const passportExpDate = p["DC PP Exp Date"] ? p["DC PP Exp Date"].split('/') : "";
                  // const memberDob = p["Member DOB"] ? p["Member DOB"].split('/') : "";

                  const memberDob = p["Member DOB"] ? new Date(p["Member DOB"]) : null;
                  const passportExpDate = p["DC PP Exp Date"] ? new Date(p["DC PP Exp Date"]) : null;
                  const object = {
                    ownerId: index + 1,
                    roles: role,
                    typeOfAddUser: "New profile",
                    partnerType: p["Related Party Type"] === "Organization" ? "Company" : "Individual",
                    isResident: p["UAE Citizen"],
                    emiratesId: p["DC Emirates ID Number"],
                    emiratesDob: {
                      day: memberDob ? memberDob.getDate().toString().padStart(2, '0') : null,
                      month: memberDob ? (memberDob.getMonth() + 1).toString().padStart(2, '0') : null,
                      year: memberDob ? memberDob.getFullYear() : null,
                    },
                    passportNumber: p["DC DED Partner Passport Number"],
                    passportExpiry: {
                      day: passportExpDate ? passportExpDate.getDate().toString().padStart(2, '0') : null,
                      month: passportExpDate ? (passportExpDate.getMonth() + 1).toString().padStart(2, '0') : null,
                      year: passportExpDate ? passportExpDate.getFullYear() : null,
                    },
                    nameEn: p["Related Party Contact Name Eng"],
                    nameAr: p["Related Party Contact Name Ara"],
                    nationality: p["DC Cust Nationality"],
                    companyNameEn: p["DC Name(ENGLISH)"],
                    companyNameAr: p["DC Name(ARABIC)"],
                    share: p["DC Ownership Percent"],
                    mobile: p["DC Mobile Number"] ? Utils.parseMobile(p["DC Mobile Number"], 'number') : '',
                    countryCode: p["DC Mobile Number"] ? Utils.parseMobile(p["DC Mobile Number"], 'cc') : '',
                    email: p["DC Email Address"],
                  };

                  const existingIndex = this.ownerList.findIndex((o: any) => o.ownerId === object.ownerId);
                  if (this.ownerList.length > 0 && existingIndex !== -1) {
                    this.ownerList[existingIndex].roles.push(role);
                  } else {
                    //this.ownerList.push(object);
                    this.ownerList = [...this.ownerList, object];
                  }
                });
              }

              //Activities
              if (serviceRequest["DC Account Activity"]) {
                let activities = Utils.checkIsArray(serviceRequest["DC Account Activity"]);
                activities?.map((activity: any) => {
                  let activityObj = {
                    codeFee: activity["DC Activity Fee"],
                    description: activity["DC Ref Description Arabic"],
                    code: activity["DC Activity Code"],
                    name: activity["DC Ref Description"],
                    descriptionOtherEn: activity["DC Special Activity Code Description (English)"],
                    descriptionOtherAr: activity["DC Special Activity Code Description (Arabic)"],
                  }
                  this.licenseActivities = [...this.licenseActivities, activityObj];
                })


              }
              this.openFolder();
            }
          }
        },
        // This method is used to handle the error callback using err
        error: (err: any) => {
          this.loader = false;
        }
      });
  }
  linkedUsers: AccountUser[] = [];
  userLoader: boolean = true
  showUnlinkConfirmation: boolean = false;
  showApproveAccessConfirmation: boolean = false;
  approvedUserLoginId: string = '';
  unlinkUserLoader: boolean = false;
  userToUnlink: AccountUser | null = null;
  // This method is used to get linked users
  getLinkedUsers() {
    let requestPayload = {
      "body": {
        "ProcessName": "DC Get Account Contacts WF",
        "DC Channel": this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
        "DC Method": DcMethodValues.MEMBERSHIP_DETAIL,
        "CSN": this.memberNumber
      }
    }
    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.RUN_PROCESS, requestPayload)
      .subscribe({
        // This method is used to handle the next callback using response
        next: (response: any) => {
          this.userLoader = false;
          if (response["Error Code"] == "" && response["Error Message"] == "") {
            if (response.SiebelMessage?.["Account"] && response.SiebelMessage?.["Account"]?.["Contact"]) {
              this.linkedUsers = Utils.checkIsArray(response.SiebelMessage?.["Account"]?.["Contact"]);
            }
          }
        },
        // This method is used to handle the error callback using err
        error: (err: any) => {
          this.userLoader = false;
        }
      });
    this.getUserDetail();
  }

  // This method is used to switch active tab and reset the embedded report outside platinum
  setActiveTab(selectedTab: string) {
    this.tab = selectedTab;
    if (this.tab != 'platinum') {
      this.setPowerBiFullscreen(false);
      this.showPowerBiReport = false;
      this.resetEmbeddedPowerBiReport();
    }
  }

  // This method is used to update tabs
  private updateTabs() {
    this.tabs = ['overview', 'owners', 'activities', 'users', 'you'];
    const platinumFlag = (this.membership as any)?.['DC Platinum Member Flag'];
    if (platinumFlag === 'Y') {
      this.tabs = [...this.tabs, 'platinum'];
    } else if (this.tab === 'platinum') {
      this.tab = 'overview';
    }
  }

  // This method is used to set the tab from query params when available
  private applyRequestedTab() {
    if (!this.requestedTab) {
      return;
    }
    if (this.tabs.includes(this.requestedTab)) {
      this.tab = this.requestedTab;
    }
  }
  pendingAccessRequest: any = [];
  // This method is used to get pending access request
  getPendingAccessRequest() {
    if (!this.isPrimaryUser) {
      return
    }
    let requestPayload = {
      "body": {
        "ProcessName": "DC Get User Access Request Count WF",
        "DC Channel": this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
        "DC Method": DcMethodValues.MEMBERSHIP_DETAIL,
        "CSN": this.memberNumber //600094
      }
    }
    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.RUN_PROCESS, requestPayload)
      .subscribe({
        next: (response: any) => {
          if (!response["Error Message"] && !response["Error Code"]) {
            let requestData = Utils.checkIsArray(response?.SiebelMessage['DC Service Request Thin BC']);
            this.pendingAccessRequest = requestData;
          }
        }
      })
  }
  userRoles(userReq: any): string {
    return [
      userReq?.['Role1'],
      userReq?.['Role2'],
      userReq?.['Role3']
    ]
      .filter(v => v && v.toString().trim() !== '')
      .join(',');
  }

  //get roles based on relationship and job title
  getRolesFromRelationshipJobTitle(
    relationshipKey: string,
    jobTitle: string,
    isAuthSignatory: 'Y' | 'N'
  ): string[] {
    let roles: string[] = [];

    // Case 1: Contact Of + Authorised Signatory
    if (relationshipKey === 'Contact Of' && jobTitle === 'Authorised Signatory') {
      roles = ['Authorised Signatory'];
    }

    // Case 2: Owner or Partner + Manager
    else if ((relationshipKey === 'Owner' || relationshipKey === 'Partner' || relationshipKey === 'Shares Owner') && jobTitle === 'Manager') {
      roles = ['Shares Owner', 'Manager'];
    }

    // Case 3: Owner or Partner + Owner
    else if ((relationshipKey === 'Owner' || relationshipKey === 'Partner') && jobTitle === 'Owner') {
      roles = ['Shares Owner'];
    }

    // Case 4: Contact Of + Manager
    else if (relationshipKey === 'Contact Of' && jobTitle === 'Manager') {
      roles = ['Manager'];
    }

    // Fallback for Owner/Partner
    if (roles.length === 0 && (relationshipKey === 'Owner' || relationshipKey === 'Partner' || relationshipKey === 'Shares Owner')) {
      roles = ['Shares Owner'];
    }

    // Add 'Authorised Signatory' if marked and not already included
    if (isAuthSignatory === 'Y' && !roles.includes('Authorised Signatory')) {
      roles.push('Authorised Signatory');
    }

    if (roles.length === 0) {
      roles = ['Manager'];
    }
    return roles;
  }

  // This method is used to check if license active using m
  isLicenseActive(m: any): boolean {
    let today = new Date();
    if (new Date(m?.['DC Expiry Date']) > today) {
      return true;
    } else {
      return false;
    }
  }

  // This method is used to apply service
  applyService() {
    this.router.navigate([RoutesPath.SERVICES_LIST]);
  }
  // This method is used to goto request
  gotoRequest(request: any) {
    this.router.navigate([`${RoutesPath.ACCESS_REQUEST_DETAIL}/${this.memberNumber}/${request['SR Number']}`]);
  }
  // This method is used to goto company page
  gotoCompanyPage() {
    this.router.navigate([RoutesPath.MEMBERSHIP.COMPLETE_COMPANY_INFORMATION])
  }
  // This method is used to update membership
  updateMembership() {
    const userDetails: any = this.sharedService.getUserProfile();
    let accounts = Utils.checkIsArray(userDetails?.Account) ?? [];
    let findAcc = accounts.find((acc) => acc.CSN == this.memberNumber);
    if (findAcc) {
      this.sharedService.setPrimaryAccount(findAcc)
    }
    this.router.navigate([RoutesPath.SERVICE + "/" + RoutesPath.MEMBERSHIP.AMENDMENT]);
  }
  // This method is used to approve reject request using type and access request
  approveRejectRequest(type: string, accessRequest: any) {
    const request = new UpsertRequestModel();
    request.body.DCMethod = (type == 'reject') ? "REJECT" : "APPROVE";
    request.body.DCLoginName = this.loginUser?.['Login Name'];
    request.body.DCLanguage = this.lang == 'ar' ? 'ARA' : 'ENU';
    request.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();
    request.body.DCIncomingHierarchy.IntObjectName = "DC Community Portal SR Upsert IO";

    let srData = request.body.DCIncomingHierarchy["ListOfDC Community Portal SR Upsert IO"]["DC Community Portal SR"]
    srData['SR Number'] = accessRequest["SR Number"];
    srData["SR Type"] = ServiceName.USER_MANAGEMENT;
    srData["Sub Type"] = ServiceName.USER_ACCESS;
    srData["CSN"] = this.memberNumber;
    this.pendingAccessRequest = this.pendingAccessRequest.filter((req: any) => req["SR Number"] != accessRequest["SR Number"])

    this.api.post(
      ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API,
      ApiEndPoints.ATA_MANAGE_SERVICE_REQUEST,
      request
    ).subscribe({
      next: (response: any) => {
        if (response['Error Code'] == null) {
          this.pendingAccessRequest = this.pendingAccessRequest.filter((req: any) => req["SR Number"] != accessRequest["SR Number"])
          this.sharedService.toastMessage('SUCCESS', 'SUCCESS', 'success');
          if (type == 'approve') {
            this.approvedUserLoginId = this.getRequestedUserLoginId(accessRequest);
            this.showApproveAccessConfirmation = true;
          }
        } else {
          this.sharedService.toastMessage('ERROR', '', 'error', response["Error Message"]);
        }
      },
      error: (err: any) => {
        this.loader = false;
      }
    });
  }

  private getRequestedUserLoginId(accessRequest: any): string {
    return (
      accessRequest?.['DC Link UserID'] ||
      accessRequest?.['Created By Name'] ||
      accessRequest?.['DC Created User Login Name'] ||
      accessRequest?.['Login Name'] ||
      ''
    ).toString();
  }

  closeApproveAccessConfirmation() {
    this.showApproveAccessConfirmation = false;
  }

  confirmApproveAccessConfirmation() {
    if (!this.approvedUserLoginId) {
      this.showApproveAccessConfirmation = false;
      this.sharedService.toastMessage('Error', 'TECHNICAL_ERROR', 'error');
      return;
    }
    this.showApproveAccessConfirmation = false;
    this.router.navigate([RoutesPath.SERVICE + "/" + RoutesPath.UPDATE_USER, this.memberNumber, this.approvedUserLoginId]);
  }
  // This method is used to download cert
  downloadCert() {
    if (!this.membership?.['DCDownloadURL']) {
      return;
    }
    window.open(this.membership?.['DCDownloadURL'], '_blank', 'noopener');
  }

  manageUsers() {
    this.setActiveTab("users");
    // this.router.navigate([`${RoutesPath.SERVICE}/${RoutesPath.MEMBERSHIP.MANAGE_SIGNATORIES}`]);

  }

  emailRelationshipManager(): void {
    const email = (this.membership as any)?.['DC First POC Email Addr'];
    if (!email) {
      return;
    }
    window.location.href = `mailto:${email}`;
  }

  // This method is used to un link user using user
  unLinkUser(user: any) {
    this.userToUnlink = user;
    this.showUnlinkConfirmation = true;
  }

  isDelinkYourself: boolean = false;
  unlinkMySelf() {
    this.isDelinkYourself = true;
    this.userToUnlink = this.loginUser;
    this.showUnlinkConfirmation = true;
  }

  // This method is used to close unlink confirmation modal
  closeUnlinkConfirmation() {
    this.isDelinkYourself = false;
    this.showUnlinkConfirmation = false;
    this.userToUnlink = null;
  }

  // This method is used to confirm unlink user
  confirmUnLinkUser() {
    if (!this.userToUnlink) {
      return;
    }
    this.unlinkUserLoader = true;
    const user = this.userToUnlink;
    const loginUserName = this.loginUser?.['Login Name'] || '';

    this.accountManagementSharedService
      .unlinkUser(
        this.memberNumber,
        loginUserName,
        user['Login Name'],
        this.lang
      )
      .subscribe({
        next: (response: any) => {
          this.unlinkUserLoader = false;
          this.showUnlinkConfirmation = false;
          this.userToUnlink = null;
          if (response["Error Code"] == null && response["Error Message"] == null) {
            this.sharedService.toastMessage('SUCCESS', 'SUCCESS', 'success');
            if (this.isDelinkYourself) {
              this.router.navigate([RoutesPath.DASHBOARD]);
            } else {
              this.linkedUsers = this.linkedUsers.filter((u: any) => u !== user);
            }
            this.isDelinkYourself = false;
          } else {
            this.sharedService.toastMessage('ERROR', '', 'error', response["Error Message"]);
            this.isDelinkYourself = false;
          }
        },
        // This method is used to handle the error callback
        error: () => {
          this.unlinkUserLoader = false;
          this.showUnlinkConfirmation = false;
          this.userToUnlink = null;
          this.isDelinkYourself = false;
        }
      });
  }

  // This method is used to open folder
  srFolder: any = null;
  mainFromSectionDocs: any[] = [];
  openFolder() {
    let isLabels = true; // true if srNumber is empty
    this.sharedService.getDocuments('', DocumentServiceType.MEMBERSHIP, isLabels)
      .subscribe({
        // This method is used to handle the next callback using response
        next: (response: any) => {
          if (response.success) {
            // If SR exists → response.data, otherwise → response
            this.srFolder = response.data ? response.data : response;
            this.mainFromSectionDocs = this.srFolder.allowedDocumentTypes?.filter((d: any) => !d.forUISection);
          } else {
            this.sharedService.toastMessage("error", response.message, 'error');
          }
          this.getMemberDocuments();
        },
        // This method is used to handle the error callback using err
        error: err => { }
      });
  }
  // This method is used to get member documents
  getMemberDocuments() {
    if (!this.memberNumber) {
      return
    }
    this.sharedService
      .getDocuments('', DocumentServiceType.MEMBERSHIP_WALLET, false, {
        memNumber: this.memberNumber,
      })
      .subscribe({
        next: (response: any) => {
          if (response.success) {
            const memberDocuments = response.data ?? response;
            if (
              !memberDocuments?.documents?.length ||
              !this.srFolder?.allowedDocumentTypes?.length
            ) {
              return;
            }
            // Build Set of allowed doc type names
            const allowedTypes = new Set(
              this.srFolder.allowedDocumentTypes.map((d: any) => d.nameEn)
            );
            // Filter matching member docs
            const matchedMemberDocs = memberDocuments.documents.filter((doc: any) =>
              allowedTypes.has(doc.documentType)
            );
            // Avoid duplicates (based on documentType + notes)
            const existingKeys = new Set(
              this.srFolder?.documents?.map(
                (d: any) => `${d.documentType}-${d.notes || ''}`
              )
            );
            let memberFolderId = memberDocuments?.folderId;
            // Filter only unique documents
            const uniqueMemberDocs = matchedMemberDocs
              .filter(
                (doc: any) =>
                  !existingKeys.has(`${doc.documentType}-${doc.notes || ''}`)
              )
              // Add memberFolderId to each document
              .map((doc: any) => ({
                ...doc,
                memberFolderId: memberFolderId ?? null,
              }));
            if (uniqueMemberDocs.length > 0) {
              // Merge safely into srFolder and notify child components
              const updatedDocuments = [
                ...(this.srFolder.documents ?? []),
                ...uniqueMemberDocs,
              ];
              this.srFolder = {
                ...(this.srFolder ?? {}),
                documents: updatedDocuments,
              };
            }
          }
        },
        error: (err) => {
          this.loader = false;
        },
      });
  }

  private isPlatinumFlag(flag: unknown): boolean {
    if (flag === null || flag === undefined) {
      return false;
    }
    const normalized = String(flag).trim().toLowerCase();
    return normalized === 'y' || normalized === 'yes' || normalized === 'true';
  }

  private resetReportFilterForm() {
    this.selectedDateFrom = null;
    this.selectedDateTo = null;
    this.showDateFromCalendar = false;
    this.showDateToCalendar = false;
    this.reportDateRangeError = false;
    this.reportDateRequiredError = false;
  }

  private validateReportDateRange(): boolean {
    const isValid = !!this.selectedDateFrom && !!this.selectedDateTo && this.selectedDateTo > this.selectedDateFrom;
    this.reportDateRangeError = !isValid;
    return isValid;
  }

  private formatDateForRequest(date: Date | null): string {
    if (!date) {
      return '';
    }
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  private formatDateForReportRequest(date: Date | null, isEndOfDay: boolean): string {
    if (!date) {
      return '';
    }

    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const time = isEndOfDay ? '23:59:59' : '00:00:00';

    return `${yyyy}-${mm}-${dd}T${time}`;
  }

  accessableServices: any = [];
  userAccessData: any;
  private getUserDetail(): void {
    this.loader = true;
    this.accountManagementSharedService
      .getUserAccessDetails(
        this.memberNumber,
        this.loginUser?.['Login Name'],
        DcMethodValues.MEMBERSHIP_DETAIL
      )
      .subscribe({
        next: (response: any) => {
          this.loader = false;
          if (response['Error Code'] == "" && response['Error Message'] == "") {
            const detail = response?.SiebelMessage?.["User Details"];
            const userAccessDetails = detail['User Access Details'];
            this.userAccessData =
              Array.isArray(userAccessDetails) && userAccessDetails.length > 0
                ? userAccessDetails[0]
                : userAccessDetails ?? null;
            this.accessableServices = mapServicesFromFlags(userAccessDetails);

          } else {
            this.sharedService.toastMessage('Error', 'TECHNICAL_ERROR', 'error');
          }
        },
        error: () => {
          this.loader = false;
        }
      });
  }

  // This method is used to open the platinum report download filter modal
  downloadPlatinumReport() {
    this.openReportFilterModal();
  }

  // This method is used to request token and view the platinum report
  viewPlatinumReport() {
    if (!this.memberNumber || this.viewReportLoader) {
      return;
    }

    this.viewReportLoader = true;
    this.powerBiError = false;
    this.api.post(ApiRoot.PLATINUM_MEMBER, ApiEndPoints.PLATINUM_MEMBER_EMBED_TOKEN, null)
      .subscribe({
        next: async (response: any) => {
          const embedToken = this.extractPowerBiEmbedToken(response);
          if (!embedToken) {
            this.viewReportLoader = false;
            this.powerBiError = true;
            this.sharedService.toastMessage('Error', 'TECHNICAL_ERROR', 'error');
            return;
          }

          this.showPowerBiReport = true;
          try {
            await this.ensurePowerBiSdkLoaded();
            this.cdr.detectChanges();
            setTimeout(() => this.embedPowerBiReport(embedToken), 0);
          } catch {
            this.viewReportLoader = false;
            this.powerBiError = true;
            this.sharedService.toastMessage('Error', 'TECHNICAL_ERROR', 'error');
          }
        },
        error: () => {
          this.viewReportLoader = false;
          this.powerBiError = true;
          this.sharedService.toastMessage('Error', 'TECHNICAL_ERROR', 'error');
        }
      });
  }

  // This method is used to open report filter modal
  openReportFilterModal() {
    if (!this.memberNumber || this.reportDownloadLoader) {
      return;
    }
    this.resetReportFilterForm();
    this.showReportFilterModal = true;
  }

  // This method is used to close report filter modal
  closeReportFilterModal() {
    this.showReportFilterModal = false;
    this.resetReportFilterForm();
  }

  // This method is used to submit platinum report download
  submitPlatinumReport() {
    if (!this.selectedDateFrom || !this.selectedDateTo) {
      this.reportDateRequiredError = true;
      this.reportDateRangeError = false;
      return;
    }

    this.reportDateRequiredError = false;
    if (!this.validateReportDateRange()) {
      return;
    }

    const fromDate = this.formatDateForReportRequest(this.selectedDateFrom, false);
    const toDate = this.formatDateForReportRequest(this.selectedDateTo, true);

    this.showReportFilterModal = false;
    this.reportDownloadLoader = true;
    this.api
      .getBlob(
        ApiRoot.PLATINUM_MEMBER,
        ApiEndPoints.PLATINUM_MEMBER_FILE_DOWNLOAD,
        {
          Member_ID: this.memberNumber,
          From_Date: fromDate,
          To_Date: toDate,
        }
      )
      .pipe(finalize(() => (this.reportDownloadLoader = false)))
      .subscribe({
        next: (response) => {
          const blob = response.body;
          if (!blob) {
            this.sharedService.toastMessage('Error', 'TECHNICAL_ERROR', 'error');
            return;
          }

          const url = window.URL.createObjectURL(blob);
          const link = window.document.createElement('a');
          link.href = url;
          link.download = this.getPlatinumReportFileName(
            response.headers.get('content-disposition')
          );
          link.click();
          window.URL.revokeObjectURL(url);
        },
        error: () => {
          this.sharedService.toastMessage('Error', 'TECHNICAL_ERROR', 'error');
        },
      });
  }

  // This method is used to toggle report date calendar
  toggleDateCalendar(target: 'from' | 'to') {
    if (target === 'from') {
      this.showDateFromCalendar = !this.showDateFromCalendar;
      this.showDateToCalendar = false;
      return;
    }
    this.showDateToCalendar = !this.showDateToCalendar;
    this.showDateFromCalendar = false;
  }

  // This method is used to clear report date from
  clearDateFrom(event?: MouseEvent) {
    event?.stopPropagation();
    this.selectedDateFrom = null;
    this.selectedDateTo = null;
    this.reportDateRangeError = false;
    this.reportDateRequiredError = false;
    this.showDateFromCalendar = false;
    this.showDateToCalendar = false;
  }

  // This method is used to clear report date to
  clearDateTo(event?: MouseEvent) {
    event?.stopPropagation();
    this.selectedDateTo = null;
    this.reportDateRangeError = false;
    this.reportDateRequiredError = false;
    this.showDateToCalendar = false;
  }

  // This method is used to handle report from date picked
  onDateFromPicked(date: Date) {
    this.selectedDateFrom = date;
    this.selectedDateTo = null;
    this.reportDateRangeError = false;
    this.reportDateRequiredError = false;
    this.showDateFromCalendar = false;
    this.showDateToCalendar = true;
  }

  // This method is used to handle report to date picked
  onDateToPicked(date: Date) {
    this.selectedDateTo = date;
    this.showDateToCalendar = false;
    this.reportDateRequiredError = false;
    this.validateReportDateRange();
  }

  // This method is used to get report date from display
  get dateFromDisplay(): string {
    return this.formatDateForRequest(this.selectedDateFrom);
  }

  // This method is used to get report date to display
  get dateToDisplay(): string {
    return this.formatDateForRequest(this.selectedDateTo);
  }

  // This method is used to get platinum report file name
  private getPlatinumReportFileName(contentDisposition: string | null): string {
    const fileNameMatch = contentDisposition?.match(
      /filename\*?=(?:UTF-8''|\"?)([^\";]+)/i
    );
    const fileName = fileNameMatch?.[1] ? decodeURIComponent(fileNameMatch[1].replace(/\"/g, '')) : '';
    return fileName || `platinum-membership-report-${this.memberNumber}.pdf`;
  }

  // This method is used to extract power bi embed token
  private extractPowerBiEmbedToken(response: any): string {
    return String(
      response?.token ??
      response?.Token ??
      response?.accessToken ??
      response?.AccessToken ??
      response?.embedToken ??
      response?.EmbedToken ??
      ''
    ).trim();
  }

  // This method is used to ensure power bi sdk loaded
  private async ensurePowerBiSdkLoaded(): Promise<void> {
    if ((window as any).powerbi) {
      return;
    }

    const existingScript = this.document.getElementById('powerbi-sdk-script') as HTMLScriptElement | null;
    if (existingScript) {
      await new Promise<void>((resolve, reject) => {
        if ((window as any).powerbi) {
          resolve();
          return;
        }
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener('error', () => reject(), { once: true });
      });
      return;
    }

    await new Promise<void>((resolve, reject) => {
      const script = this.document.createElement('script');
      script.id = 'powerbi-sdk-script';
      script.src = PLATINUM_POWER_BI.sdkUrl;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject();
      this.document.body.appendChild(script);
    });
  }

  // This method is used to embed power bi report
  private embedPowerBiReport(embedToken: string): void {
    const container = this.powerBiContainer?.nativeElement;
    const powerbi = (window as any).powerbi;
    const models = (window as any)['powerbi-client']?.models || (window as any).models;

    if (!container || !powerbi || !models) {
      this.viewReportLoader = false;
      this.powerBiError = true;
      this.sharedService.toastMessage('Error', 'TECHNICAL_ERROR', 'error');
      return;
    }

    const embedUrl = buildPlatinumReportEmbedUrl(this.memberNumber);
    powerbi.reset(container);
    this.applyPowerBiContainerHeight();

    const config = {
      type: 'report',
      tokenType: models.TokenType.Embed,
      accessToken: embedToken,
      embedUrl,
      id: PLATINUM_POWER_BI.reportId,
      permissions: models.Permissions.All,
      settings: {
        panes: {
          filters: {
            visible: false
          },
          pageNavigation: {
            visible: true
          }
        }
      }
    };

    this.embeddedPowerBiReport = powerbi.embed(container, config);
    this.embeddedPowerBiReport.off('loaded');
    this.embeddedPowerBiReport.on('loaded', () => {
      this.viewReportLoader = false;
    });

    this.embeddedPowerBiReport.off('rendered');
    this.embeddedPowerBiReport.on('rendered', () => {
      this.applyPowerBiContainerHeight();
      this.viewReportLoader = false;
      this.powerBiError = false;
    });

    this.embeddedPowerBiReport.off('error');
    this.embeddedPowerBiReport.on('error', () => {
      this.viewReportLoader = false;
      this.powerBiError = true;
      this.sharedService.toastMessage('Error', 'TECHNICAL_ERROR', 'error');
    });
  }

  // This method is used to reset embedded power bi report
  private resetEmbeddedPowerBiReport(): void {
    const container = this.powerBiContainer?.nativeElement;
    const powerbi = (window as any).powerbi;
    if (container && powerbi) {
      powerbi.reset(container);
    }
    this.embeddedPowerBiReport = null;
  }

  // This method is used to toggle the power bi report in full page mode
  togglePowerBiFullscreen(): void {
    this.setPowerBiFullscreen(!this.isPowerBiFullscreen);
  }

  // This method is used to update the power bi fullscreen page state
  private setPowerBiFullscreen(isFullscreen: boolean): void {
    this.isPowerBiFullscreen = isFullscreen;
    this.document.body.classList.toggle('power-bi-fullscreen-open', isFullscreen);
    queueMicrotask(() => this.applyPowerBiContainerHeight());
  }

  // This method is used to apply the correct report height for normal and full page mode
  private applyPowerBiContainerHeight(): void {
    const container = this.powerBiContainer?.nativeElement;
    if (!container) {
      return;
    }

    const reportHeight = this.isPowerBiFullscreen ? 'calc(100vh - 140px)' : '999px';
    container.style.height = reportHeight;
    container.style.minHeight = reportHeight;

    const iframe = container.querySelector('iframe');
    if (iframe) {
      iframe.style.height = reportHeight;
    }

    if (this.embeddedPowerBiReport?.resize) {
      setTimeout(() => this.embeddedPowerBiReport.resize(), 0);
    }
  }
}
